/**
 * Layout Type Definitions and Constants
 * Defines the structure and configuration for portal layouts
 */

export const PORTAL_TYPES = {
  BUREAU: 'bureau',
  ADMIN: 'admin',
  CLIENT: 'client',
  SUPER_ADMIN: 'super-admin',
  TIPLOCK: 'tiplock',
};

export const LAYOUT_STYLES = {
  FULL_WIDTH: 'full-width',
  WITH_SIDEBAR: 'with-sidebar',
  FLOATING_SIDEBAR: 'floating-sidebar',
  MINIMAL: 'minimal',
};

export const NAVIGATION_TYPES = {
  TOP_ONLY: 'top-only',
  SIDEBAR_ONLY: 'sidebar-only',
  TOP_AND_SIDEBAR: 'top-and-sidebar',
  MINIMAL: 'minimal',
};

export const BREAKPOINTS = {
  LAPTOP: 'md', // 900px - Small laptops
  DESKTOP: 'lg', // 1400px - Standard desktops
  MONITOR: 'xl', // 1536px - Large monitors/4K displays
};

/**
 * Default layout configuration
 */
export const DEFAULT_LAYOUT_CONFIG = {
  showHeader: true,
  showSidebar: true,
  showFooter: false,
  showBreadcrumbs: true,
  sidebarCollapsible: true,
  sidebarDefaultOpen: true,
  headerHeight: 64,
  sidebarWidth: 280,
  sidebarCollapsedWidth: 60,
  contentPadding: 24,
  // Desktop-specific responsive configurations
  responsive: {
    laptop: {
      sidebarWidth: 260,
      contentPadding: 20,
      headerHeight: 64,
    },
    desktop: {
      sidebarWidth: 300,
      contentPadding: 24,
      headerHeight: 68,
    },
    monitor: {
      sidebarWidth: 340,
      contentPadding: 32,
      headerHeight: 72,
    },
  },
};

/**
 * Portal-specific layout configurations
 */
export const PORTAL_LAYOUT_CONFIGS = {
  [PORTAL_TYPES.BUREAU]: {
    ...DEFAULT_LAYOUT_CONFIG,
    layoutStyle: LAYOUT_STYLES.FLOATING_SIDEBAR,
    navigationType: NAVIGATION_TYPES.TOP_AND_SIDEBAR,
    sidebarFloating: true,
    showFooter: false,
  },
  [PORTAL_TYPES.ADMIN]: {
    ...DEFAULT_LAYOUT_CONFIG,
    layoutStyle: LAYOUT_STYLES.FULL_WIDTH,
    navigationType: NAVIGATION_TYPES.TOP_ONLY,
    showSidebar: false,
    showFooter: true,
    backgroundGradient: true,
  },
  [PORTAL_TYPES.CLIENT]: {
    ...DEFAULT_LAYOUT_CONFIG,
    layoutStyle: LAYOUT_STYLES.FLOATING_SIDEBAR,
    navigationType: NAVIGATION_TYPES.TOP_AND_SIDEBAR,
    sidebarFloating: true,
    showFooter: false,
  },
  [PORTAL_TYPES.SUPER_ADMIN]: {
    ...DEFAULT_LAYOUT_CONFIG,
    layoutStyle: LAYOUT_STYLES.WITH_SIDEBAR,
    navigationType: NAVIGATION_TYPES.TOP_AND_SIDEBAR,
    sidebarFloating: false,
    showFooter: true,
    sidebarDefaultOpen: true,
  },
  [PORTAL_TYPES.TIPLOCK]: {
    ...DEFAULT_LAYOUT_CONFIG,
    layoutStyle: LAYOUT_STYLES.FLOATING_SIDEBAR,
    navigationType: NAVIGATION_TYPES.TOP_AND_SIDEBAR,
    sidebarFloating: true,
    showFooter: false,
  },
};

/**
 * Enhanced theme configurations for each portal with desktop-optimized styling
 */
export const PORTAL_THEME_CONFIGS = {
  [PORTAL_TYPES.BUREAU]: {
    primaryColor: '#1976d2',
    primaryColorDark: '#90caf9',
    secondaryColor: '#42a5f5',
    accentColor: '#1e88e5',
    backgroundLight: '#f8fafc',
    backgroundDark: '#0f172a',
    paperLight: '#ffffff',
    paperDark: '#1e293b',
    gradientLight: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
    gradientDark: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    glassMorphismLight: 'rgba(255, 255, 255, 0.85)',
    glassMorphismDark: 'rgba(30, 41, 59, 0.85)',
    borderLight: 'rgba(148, 163, 184, 0.1)',
    borderDark: 'rgba(148, 163, 184, 0.1)',
  },
  [PORTAL_TYPES.ADMIN]: {
    primaryColor: '#1976d2',
    primaryColorDark: '#90caf9',
    secondaryColor: '#3f51b5',
    accentColor: '#2196f3',
    backgroundLight: '#e3f2fd',
    backgroundDark: '#0d1421',
    paperLight: '#ffffff',
    paperDark: '#1a2332',
    gradientLight:
      'linear-gradient(135deg, rgba(227, 242, 253, 0.95) 0%, rgba(187, 222, 251, 0.95) 100%)',
    gradientDark: 'linear-gradient(135deg, rgba(13, 20, 33, 0.95) 0%, rgba(26, 35, 50, 0.95) 100%)',
    glassMorphismLight: 'rgba(255, 255, 255, 0.9)',
    glassMorphismDark: 'rgba(26, 35, 50, 0.9)',
    borderLight: 'rgba(25, 118, 210, 0.1)',
    borderDark: 'rgba(144, 202, 249, 0.1)',
  },
  [PORTAL_TYPES.CLIENT]: {
    primaryColor: '#2e7d32',
    primaryColorDark: '#66bb6a',
    secondaryColor: '#388e3c',
    accentColor: '#4caf50',
    backgroundLight: '#d8d9dce6',
    backgroundDark: '#0d1b0f',
    paperLight: '#ffffff',
    paperDark: '#1b2e20',
    gradientLight: '#d8d9dce6',
    gradientDark: 'linear-gradient(135deg, #0d1b0f 0%, #1b2e20 100%)',
    glassMorphismLight: 'rgba(255, 255, 255, 0.9)',
    glassMorphismDark: 'rgba(27, 46, 32, 0.9)',
    borderLight: 'rgba(46, 125, 50, 0.1)',
    borderDark: 'rgba(102, 187, 106, 0.1)',
  },
  [PORTAL_TYPES.SUPER_ADMIN]: {
    primaryColor: '#d32f2f',
    primaryColorDark: '#ef5350',
    secondaryColor: '#c62828',
    accentColor: '#f44336',
    backgroundLight: '#fff3e0',
    backgroundDark: '#0d1117',
    paperLight: '#ffffff',
    paperDark: '#21262d',
    gradientLight: 'linear-gradient(135deg, #fff3e0 0%, #ffccbc 100%)',
    gradientDark: 'linear-gradient(135deg, #0d1117 0%, #21262d 100%)',
    glassMorphismLight: 'rgba(255, 255, 255, 0.9)',
    glassMorphismDark: 'rgba(33, 38, 45, 0.9)',
    borderLight: 'rgba(211, 47, 47, 0.1)',
    borderDark: 'rgba(239, 83, 80, 0.1)',
  },
  [PORTAL_TYPES.TIPLOCK]: {
    primaryColor: '#6B5B95',
    primaryColorDark: '#9B8FC4',
    secondaryColor: '#7D6BA0',
    accentColor: '#8B7BAA',
    backgroundLight: '#f5f3f7',
    backgroundDark: '#1a1625',
    paperLight: '#ffffff',
    paperDark: '#252035',
    gradientLight: 'linear-gradient(135deg, #f5f3f7 0%, #e8e4ed 100%)',
    gradientDark: 'linear-gradient(135deg, #1a1625 0%, #252035 100%)',
    glassMorphismLight: 'rgba(255, 255, 255, 0.9)',
    glassMorphismDark: 'rgba(37, 32, 53, 0.9)',
    borderLight: 'rgba(107, 91, 149, 0.1)',
    borderDark: 'rgba(155, 143, 196, 0.1)',
  },
};
